// src/pages/SettingPage/tabs/RoleManagementTab.js
import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, message, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { db } from '../../../firebase-config';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs } from 'firebase/firestore';

const RoleManagementTab = ({ roles: rolesProp, onRolesUpdate }) => {
  // 親から roles が渡されたら controlled とみなす
  const controlled = Array.isArray(rolesProp);
  const [roles, setRoles] = useState(rolesProp ?? []);
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form] = Form.useForm();

  const rolesCol = collection(db, 'roles');

  // 親からの roles 変化を反映
  useEffect(() => {
    if (controlled) setRoles(rolesProp ?? []);
  }, [controlled, rolesProp]);

  // controlled でない場合は自動取得
  const fetchRoles = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(rolesCol);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRoles(list);
    } catch (e) {
      console.error(e);
      message.error('役割一覧を読み込めませんでした。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!controlled) fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlled]);

  const refresh = async () => {
    if (controlled) {
      onRolesUpdate && onRolesUpdate();
    } else {
      await fetchRoles();
    }
  };

  const showModal = (role = null) => {
    setEditingRole(role);
    form.setFieldsValue(
      role ? { name: role.name ?? '', description: role.description ?? '' } : { name: '', description: '' }
    );
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setEditingRole(null);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingRole) {
        await updateDoc(doc(db, 'roles', editingRole.id), values);
        message.success('役割を更新しました。');
      } else {
        await addDoc(rolesCol, values);
        message.success('役割を追加しました。');
      }
      handleCancel();
      refresh();
    } catch (error) {
      console.error('役割の保存中にエラー:', error);
      message.error('エラーが発生しました。');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'roles', id));
      message.success('役割を削除しました。');
      refresh();
    } catch (error) {
      console.error(error);
      message.error('役割の削除中にエラーが発生しました。');
    }
  };

  // ⚠️ Không dùng useMemo để tránh warning thiếu dependency
  const columns = [
    { title: '役割名', dataIndex: 'name', key: 'name' },
    { title: '説明', dataIndex: 'description', key: 'description' },
    {
      title: '操作',
      key: 'action',
      width: 220,
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => showModal(record)}>編集</Button>
          <Popconfirm
            title="役割を削除"
            description="この役割を削除してもよろしいですか？（元に戻せません）"
            onConfirm={() => handleDelete(record.id)}
            okText="削除"
            cancelText="キャンセル"
          >
            <Button icon={<DeleteOutlined />} danger>削除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => showModal()}
        style={{ marginBottom: 16 }}
      >
        新しい役割を追加
      </Button>

      <Table
        columns={columns}
        dataSource={(roles ?? []).map((r) => ({ ...r, key: r.id }))}
        rowKey="id"
        loading={loading}
        bordered
      />

      <Modal
        title={editingRole ? '役割を編集' : '新しい役割を追加'}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={editingRole ? '更新' : '追加'}
        cancelText="キャンセル"
        forceRender
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="役割名"
            rules={[{ required: true, message: '役割名を入力してください。' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="説明">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RoleManagementTab;
